import {
  LabTest,
  LabTestResult,
  LabTestTemplate,
  LabTestTemplateParametersFreeform,
  LabTestTemplateParametersMulti,
  LabTestTemplateParametersNested,
  LabTestTemplateParametersSingle,
  LabTestTemplateParametersType, LabTestTemplateType,
} from "../types";
import {ReactElement, useEffect, useState} from "react";
import {Button, CircularProgress, Typography} from "@mui/material";
import {StyledBoxColumn, StyledBoxRow, StyledSpan} from "@/shared/StyledComponents";
import {EditOutlined, HistoryOutlined} from "@mui/icons-material";
import {Action} from "@/shared/types";
import {FormGenMultiStepHelper} from "@/shared/FormGenMultiStepHelper";
import {PAGE_FRAGMENT_LG_WIDTH, PD_MD, PD_XXSM} from "@/shared/dimens";
import {colorRed} from "@/shared/colors";
import {FormGenContainer, FormGenContainerMode} from "@/shared/FormGenContainer";
import {LabTestResultView} from "./LabTestResultView";
import {Api} from "../api";
import {BaseApp, DIALOG_FLAG_DISABLE_BACKDROP_CLICK, DIALOG_FLAG_SHOW_CLOSE} from "../../shared/BaseApp";

export function EditLabTestTemplateButton({labTest, templates, setTemplates}: {
  labTest: LabTest,
  templates: LabTestTemplate[],
  setTemplates: (templates: LabTestTemplate[]) => void
}): ReactElement {
  const editing = Boolean(templates.length > 0);
  const [loading, setLoading] = useState<boolean>(!(templates?.length > 0));
  useEffect(() => {
    if (loading) {
      (async function () {
        if (labTest.typeId) {
          const masterTemplate = await Api.getInstance().fetchLabTestTemplateById(labTest.typeId);
          masterTemplate.labTest = labTest;
          setTemplates([masterTemplate]);
        }
        setLoading(false);
      })();
    }
  }, [loading]);
  return !loading
    ? <div style={{display: "flex", flexDirection: "column", gap: PD_XXSM}}>
      <StyledBoxRow>
        <Button
          startIcon={<EditOutlined/>}
          variant={"outlined"}
          onClick={() => {
            if (!editing) {
              EditLabTestTemplateHelper.createLabTestTemplate(labTest, (template: LabTestTemplate) => {
                setTemplates([...templates, template]);
              });
            } else {
              EditLabTestTemplateHelper.editLabTestTemplate(templates[templates.length - 1], (template: LabTestTemplate) => {
                setTemplates([...templates, template]);
              });
            }
          }}>
          {!editing ? "Create" : "Edit"} Template
        </Button>
        {templates.length > 0
          ? <Button onClick={() => EditLabTestTemplateHelper.viewLabTestTemplate(templates[templates.length - 1])}>
            View
          </Button>
          : null}
        {templates.length > 1
          ? <Button startIcon={<HistoryOutlined/>}>
            History
          </Button>
          : null}
        <StyledSpan/>
      </StyledBoxRow>
      {templates.length === 0
        ? <Typography variant={"caption"} style={{color: colorRed}}>
          No template available for this test. You must create one to save.
        </Typography>
        : null}
    </div>
    : <CircularProgress/>;
}

export function renderEditLabTestTemplateButton(labTest: LabTest, templates: LabTestTemplate[], setTemplates: (templates: LabTestTemplate[]) => void): ReactElement {
  return <EditLabTestTemplateButton labTest={labTest} templates={templates} setTemplates={setTemplates}/>;
}

// (??) Need to redefine here or else LabTestTemplateType below ends up with some weird array-like structure.
// export enum LabTestTemplateType {
//   SINGLE = "single",
//   MULTI = "multi",
//   NESTED = "nested",
//   FREEFORM = "freeform",
// }

export function renderLabTestTemplateParameters(type: LabTestTemplateType, parameters: LabTestTemplateParametersType, setParameters: (parameters: LabTestTemplateParametersType) => void): ReactElement {
  if (parameters?.paramsType !== type) {
    switch (type) {
      case LabTestTemplateType.SINGLE:
        parameters = new LabTestTemplateParametersSingle();
        break;
      case LabTestTemplateType.MULTI:
        parameters = new LabTestTemplateParametersMulti();
        break;
      case LabTestTemplateType.NESTED:
        parameters = new LabTestTemplateParametersNested();
        break;
      case LabTestTemplateType.FREEFORM:
        parameters = new LabTestTemplateParametersFreeform();
        break;
    }
    setTimeout(() => setParameters(parameters), 1);
  }
  return <FormGenContainer content={parameters}/>;
}

export function renderLabTestTemplatePreview(template: LabTestTemplate): ReactElement {
  return <LabTestResultView
    result={LabTestResult.createNewForLabTest(template.labTest as LabTest, template)}
  />;
}

export class EditLabTestTemplateHelper {

  static createLabTestTemplate(labTest: LabTest, onFinish: (template: LabTestTemplate) => void): void {
    const template = LabTestTemplate.createNewForLabTest(labTest);
    FormGenMultiStepHelper.showDialog(template, {
      title: "New Lab Test template",
      finishAction: new Action("Finish", () => onFinish(template)),
      steps: LabTestTemplate.multiStepConfigSteps(),
    }, true, {containerMode: FormGenContainerMode.CREATE});
  }

  static editLabTestTemplate(template: LabTestTemplate, onFinish: (template: LabTestTemplate) => void): void {
    FormGenMultiStepHelper.showDialog(template, {
      title: "Edit Lab Test template",
      finishAction: new Action("Finish", () => onFinish(template)),
      steps: LabTestTemplate.multiStepConfigSteps(),
    }, true, {containerMode: FormGenContainerMode.EDIT});
  }

  static viewLabTestTemplate(template: LabTestTemplate): void {
    BaseApp.CONTEXT.showDialog({flags: DIALOG_FLAG_SHOW_CLOSE | DIALOG_FLAG_DISABLE_BACKDROP_CLICK}, () =>
      <StyledBoxColumn style={{width: "84dvw", maxWidth: PAGE_FRAGMENT_LG_WIDTH, padding: PD_MD}}>
        <Typography variant={"h5"}>{template.labTest.name}</Typography>
        <LabTestResultView result={LabTestResult.createNewForLabTest(template.labTest as LabTest, template)}/>
      </StyledBoxColumn>);
  }
}